import { useI18n } from 'vue-i18n'
import { isImageMime, isInlineViewable, type UserFileKind } from 'models'
import { useImageFallback } from './useImageFallback'

/** Minimal shape a file-list row needs for the shared display helpers. */
export interface FileRowLike {
    documentId: string
    mimeType: string | null
}

/**
 * Shared display logic for the user-files list rows, used by both the in-app
 * table (PjUserFilesTable) and the embedded widget (PkChatbotViewFiles). The two
 * surfaces differ only in how they build URLs (authenticated store vs widget API
 * client), so the URL builders are injected.
 *
 * - `view(id, inline)` → opens/downloads the original file.
 * - `preview(id)`      → small resized blob for the row thumbnail.
 */
export function useFileRow(urls: {
    view: (documentId: string, inline: boolean) => string
    preview: (documentId: string) => string
}) {
    const { locale } = useI18n({ useScope: 'global' })

    // Image rows show a thumbnail; if it fails to load (auth/redirect, or the
    // preview isn't generated yet) we fall back to the file-type icon.
    const { canShowImage, onImageError } = useImageFallback()
    const thumbUrl = (file: FileRowLike): string =>
        urls.preview(file.documentId)
    const showThumb = (file: FileRowLike): boolean =>
        isImageMime(file.mimeType) && canShowImage(thumbUrl(file))
    const onThumbError = (file: FileRowLike): void => {
        onImageError(thumbUrl(file))
    }

    // Name click previews images/PDF inline; an explicit download forces an
    // attachment (inline=false → download SAS).
    const openFile = (file: FileRowLike, download = false): void => {
        const inline = !download && isInlineViewable(file.mimeType)
        window.open(urls.view(file.documentId, inline), '_blank', 'noopener')
    }

    const formatDate = (iso: string): string =>
        new Date(iso).toLocaleDateString(locale.value, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })

    return { showThumb, thumbUrl, onThumbError, openFile, formatDate }
}

/** What tells a chat file row (a spreadsheet, a Word document) apart from a KB document. */
export interface FileKindLike {
    kind?: UserFileKind
    sourceAttachmentId?: string | null
    createdByAgent?: boolean
}

/**
 * The wording that depends on what a "My files" row is. A KB document is a
 * copy of the upload: removing it only takes it out of the assistant's reach.
 * A spreadsheet or a Word document is the chat attachment itself, uploaded or
 * created by the assistant, and never enters the KB: its row says so, and
 * removing it deletes it from its chat with every version the assistant wrote
 * of it.
 */
export function useUserFileKind() {
    const { t } = useI18n({ useScope: 'global' })

    const isChatFile = (file: FileKindLike): boolean =>
        file.kind === 'spreadsheet' || file.kind === 'wordDocument'

    /** A short note for the row; undefined for a KB document. */
    const kindNote = (file: FileKindLike): string | undefined => {
        if (!isChatFile(file)) {
            return undefined
        }
        if (file.sourceAttachmentId) {
            return t('userFiles.chatFileVersion')
        }
        return file.createdByAgent
            ? t('userFiles.chatFileCreated')
            : t('userFiles.chatFileInChatOnly')
    }

    const removeLabel = (file: FileKindLike): string =>
        isChatFile(file)
            ? t('userFiles.deleteChatFile')
            : t('userFiles.exclude')

    /** The options of the confirmation dialog before removing the file. */
    const removeConfirm = (file: FileKindLike) =>
        isChatFile(file)
            ? {
                  title:
                      file.kind === 'wordDocument'
                          ? t('confirmTitle.deleteWordDocument')
                          : t('confirmTitle.deleteSpreadsheet'),
                  questionLabel: file.createdByAgent
                      ? t('userFiles.deleteCreatedFileConfirm')
                      : t('userFiles.deleteChatFileConfirm'),
              }
            : {
                  title: t('confirmTitle.excludeUserFile'),
                  questionLabel: t('userFiles.excludeConfirm'),
              }

    return { isChatFile, kindNote, removeLabel, removeConfirm }
}
